import { useEffect, useState } from "react";

export enum ScriptLoadStatus {
  Idle = "idle",
  Loading = "loading",
  Ready = "ready",
  Error = "error",
}

export const useHeadScript = (src: string, type?: string) => {
  const [status, setStatus] = useState(src ? ScriptLoadStatus.Loading : ScriptLoadStatus.Idle);

  useEffect(() => {
    if (!src) {
      setStatus(ScriptLoadStatus.Idle);
      return;
    }

    // Fetch existing script element by src
    // It may have been added by another instance of this hook
    let script = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);

    if (!script) {
      // Create script
      script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.setAttribute("data-status", "loading");
      if (type) {
        script.type = type;
      }
      // Add script to document body
      document.head.appendChild(script);

      // Store status in attribute on script
      // This can be read by other instances of this hook
      const setAttributeFromEvent = (event: Event) => {
        script?.setAttribute(
          "data-status",
          event.type === "load" ? ScriptLoadStatus.Ready : ScriptLoadStatus.Error
        );
      };

      script.addEventListener("load", setAttributeFromEvent);
      script.addEventListener("error", setAttributeFromEvent);
    } else {
      // Grab existing script status from attribute and set to state.
      setStatus(script.getAttribute("data-status") as ScriptLoadStatus);
    }

    // Script event handler to update status in state
    // Note: Even if the script already exists we still need to add
    // event handlers to update the state for *this* hook instance.
    const setStateFromEvent = (event: Event) => {
      setStatus(event.type === "load" ? ScriptLoadStatus.Ready : ScriptLoadStatus.Error);
      if (event.type !== "load") {
        console.log(`${src} script failed to load`);
        document.body.removeChild(script as Node);
      }
    };

    // Add event listeners
    script.addEventListener("load", setStateFromEvent);
    script.addEventListener("error", setStateFromEvent);

    // Remove event listeners on cleanup
    return () => {
      if (script) {
        script.removeEventListener("load", setStateFromEvent);
        script.removeEventListener("error", setStateFromEvent);
      }
    };
  }, [src, type]);

  return status;
};
